<template>
  <div>
    <h4>Basic details</h4>
    <sme-card large>
      <div class="card-container">
        <app-input
          :value="name"
          :validate-immediate="false"
          name="Award name"
          description="3-50 characters"
          rules="required|min:3|max:50"
          type="text"
          placeholder="Keep it casual and encouraging"
          class="m-0"
          @input="$emit('update:name', $event)"
        >
          <template #label>
            <div class="label">
              Award name
              <small>Give this award a clear, meaningful name.</small>
            </div>
          </template>
        </app-input>

        <hr />

        <div class="label">
          Award scope
          <small>
            Choose who can see and earn this award. Leave a field empty to include everyone. If you set more than one,
            an employee must match all of them.
          </small>
        </div>

        <multi-select
          v-for="field in scopeFields"
          :id="`scope-${field.key}`"
          :key="field.key"
          :value="scope[field.key] === undefined ? [] : scope[field.key]"
          :options="optionsFor(field)"
          placeholder="Select who will receive the award"
          :select-all="true"
          :select-all-text="`All ${field.label.toLowerCase()}`"
          :empty-means-all="true"
          :require-explicit-all="field.key === 'division' && requireExplicitScope"
          searchable
          :search-min-chars="0"
          :search-placeholder="`Search ${field.label.toLowerCase()}...`"
          class="m-0"
          @input="$emit('update:scope', { ...scope, [field.key]: $event })"
        >
          <template #label>
            <div class="label">{{ field.label }}</div>
          </template>
        </multi-select>
        <div v-for="entry in unregisteredEntries" :key="entry.key">
          <multi-select
            :id="`scope-${entry.key}`"
            :value="entry.values"
            :options="entry.values.map(v => ({ text: v, value: v }))"
            :select-all="false"
            class="m-0"
            @input="$emit('update:scope', scopeWithValues(scope, entry.key, $event))"
          >
            <template #label>
              <div class="label">{{ unregisteredFieldLabel(entry.key) }}</div>
            </template>
          </multi-select>
          <small class="text-danger d-block mt-2">
            This field is no longer available. Remove a value to stop restricting by it.
          </small>
        </div>
        <small v-if="scopeError" class="text-danger">{{ scopeError }}</small>

        <slot name="after-divisions" />
      </div>
    </sme-card>
  </div>
</template>

<script setup>
import AppInput from '@/components/AppInput.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import { computed } from 'vue';
import { scopeWithValues, unregisteredFieldLabel, unregisteredScopeEntries } from '@/pages/recognition/constants';

const optionsFor = field => field.values.map(v => ({ text: v.label, value: v.value }));

const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  // Per field: [] = everyone, [values] = those values, null = not chosen yet.
  scope: {
    type: Object,
    default: () => ({}),
  },
  scopeFields: {
    type: Array,
    default: () => [],
  },
  // Create forms opt in so scope has to be chosen (B2B-144); edit forms leave this off
  // because the award being edited always has an explicit scope already.
  requireExplicitScope: {
    type: Boolean,
    default: false,
  },
  scopeError: {
    type: String,
    default: '',
  },
});

defineEmits(['update:name', 'update:scope']);

const unregisteredEntries = computed(() => unregisteredScopeEntries(props.scope, props.scopeFields));
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
